// UTILS
import { nextTick } from "vue"
import { setup, createActor, fromPromise } from "xstate"
import { storeToRefs } from "pinia"
import router from "@/router"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"

// TYPES
import type { BrainMachineEventType } from "./types"
import { type GlobalMachineEvent } from "@/machines/types"

// STORES
import { useAnswersStore } from "@/features/brain/stores/answers.store"
import { useBrainTutoStore } from "@/features/brain/stores/brainTuto.store"
import { useUserStore } from "@/stores/user"
import { useI18nStore } from "@/stores/i18n"

// MACHINE
import { globalActor } from "@/machines/globalMachine"

// HELPERS
import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useHelpStore } from "@/components/af/Help/help.store"
import { withAssessmentLoadingMinDuration } from "@/features/assessment-loading/assessmentLoadingDuration"

// Loader for brain data
// to redirect to the right Brain step
const loadBrainData = fromPromise(() =>
  withAssessmentLoadingMinDuration("BRAIN", async () => {
    const { getBrainStatus } = storeToRefs(useUserStore())
    const { showLang } = storeToRefs(useI18nStore())
    const { getBrain, initBrain, fetchAudios } = useAnswersStore()
    const { totalAnswers } = storeToRefs(useAnswersStore())

    let data
    if (getBrainStatus.value === null) {
      await initBrain()
      await fetchAudios()
      // Brand new test — the language can still be changed
      showLang.value = true
      return { status: "new" }
    } else {
      data = await getBrain()
      await fetchAudios()

      const assessmentSummary =
        useUserStore().assessmentSummary.assessments.brain
      const isFinisedAndNotPassed =
        data?.status === "finish" && assessmentSummary?.not_passed

      // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
      let assessmentStatus = isFinisedAndNotPassed ? "todo" : data?.status

      // The language can only be changed as long as the test is not started
      showLang.value = assessmentStatus !== "inprogress"

      switch (assessmentStatus) {
        case "todo":
          return { status: "new" }

        case "inprogress":
          // le test n'est pas terminé si moins de 10 réponses
          if (totalAnswers.value < 10) {
            return { status: "showQuestions" }
          }
          // If the user has complete every steps but status is inprogress, we need to send the results to backend
          else {
            return { status: "showLoadResults" }
          }

        case "redirect-contact":
          globalActor.send({ type: "GLOBAL.GUEST.CONTACTSUPPORT" })
          return

        case "thank-you":
          router.replace({ name: "thanksATS" })
          return

        case "finish":
        default:
          return { status: "showFinal" }
      }
    }
  })
)

export const brainMachine = setup({
  actors: {
    loadBrainData,
  },
  types: {} as {
    events: BrainMachineEventType
  },
  actions: {
    openTutoEndModal: () => useBrainTutoStore().openTutoEndModal(),
  },
}).createMachine({
  id: "brainMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "brainLoading") {
            router.replace({ name: "brainLoading" })
          }
        },
      ],
      invoke: {
        id: "loadBrain",
        src: "loadBrainData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "tuto",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "brainResume" })],
      on: {
        done: {
          target: "resume",
        },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "brainTuto" })],
      on: {
        done: {
          target: "tutoConfirmation",
        },
      },
    },
    tutoConfirmation: {
      entry: ["openTutoEndModal"],
      on: {
        done: {
          target: "test",
        },
        restart: {
          target: "tuto",
        },
      },
    },
    resume: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "brainResume") {
            router.replace({ name: "brainResume" })
          }
        },
      ],
      on: {
        done: {
          target: "test",
        },
      },
    },
    test: {
      entry: [() => router.replace({ name: "brainTest" })],
      on: {
        done: {
          target: "loadResults",
        },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "brainLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export let brainActor = createActor(brainMachine)

const brainActorObserver = {
  async complete() {
    const { isFromInt } = storeToRefs(useUserStore())
    const { getSessionItem } = useSessionStorage()

    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())
    const { af_workflow_uuid, af_retake } = storeToRefs(useHelpStore())

    const retake = af_retake.value ?? useCookies().get(COOKIES.RETAKE)

    await fetchNextAssessment(af_workflow_uuid.value ?? null, retake ?? false)

    await nextTick()

    if (isFromInt.value && getSessionItem("goto_param")) {
      const didRedirect = await redirectForInt()
      if (didRedirect) return
    }

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID)
      useCookies().remove(COOKIES.RETAKE)
      af_workflow_uuid.value = ""
      af_retake.value = false

      const didRedirect = await redirectForInt()
      if (didRedirect) return

      globalActor.send({ type: "GLOBAL.BRAIN.DONE" })
    }
  },
}

brainActor.subscribe(brainActorObserver)

async function redirectForInt(): Promise<boolean> {
  const { isFromInt } = storeToRefs(useUserStore())
  const { getSessionItem, deleteSessionItem } = useSessionStorage()

  if (isFromInt.value) {
    if (getSessionItem("goto_param")) {
      deleteSessionItem("goto_param")
    }

    if (getSessionItem("redirect_url")) {
      let redirectUrl = getSessionItem("redirect_url") as string
      deleteSessionItem("redirect_url")
      // Redirect to custom page
      window.location.href = redirectUrl
      return true
    } else {
      // Redirect to thanksATS route
      await router.push({ name: "thanksATS" })
      return true
    }
  }

  return false
}

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const brainActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToBrainActor(observer: (_snapshot: unknown) => void) {
  brainActorObservers.push(observer)
  brainActor.subscribe(observer)
}

let hasBrainActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startBrainActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "brain"

  if (hasBrainActorStarted && isEnteringAssessment) {
    brainActor.stop()
    brainActor = createActor(brainMachine)
    brainActor.subscribe(brainActorObserver)
    for (const observer of brainActorObservers) brainActor.subscribe(observer)
  }

  hasBrainActorStarted = true
  brainActor.start()
}
